import React, { useState, useEffect, useMemo, useContext, useRef } from "react";
import { EventsStoreContext } from "../../Store/EventsStore";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { getEvents } from "../../Requests/EventService.ts";
import { Box, Typography, Button, Chip, Avatar } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Add } from "@mui/icons-material";
import AddModalView from "../AddModalView/AddModalView";
import AddEvent from "./AddEvent";
import { VISIBLE_STATE_CASES } from "../../Models/StateConstants.ts";
import { getEventCategories } from "../../Requests/EventCategoriesService.ts";
import { getSpeakers } from "../../Requests/SpeakerService.ts";
import { EVENT_REGISTRATION_STATUS } from "../../Models/StateConstants.ts";
import { addPinControlsToColumns, createTogglePinHandler } from '../../Helpers/ColumnPinningHelper.jsx';
import { useRestoreScrollPosition } from '../../Hooks/useScrollRestoration';
import { useTableState } from '../../Hooks/useTableState';
import { useFilteredBySearch } from '../../Hooks/useFilteredBySearch';
import TableToolbarWithSearch from '../TableToolbar/TableToolbarWithSearch.jsx';
import ColumnsIcon from '../TableToolbar/ColumnsIcon.jsx';

const SEARCH_FIELD_OPTIONS = [
  { value: 'title', label: 'Title' },
  { value: 'venue', label: 'Venue' },
  { value: 'speaker', label: 'Speaker name' },
  { value: 'guest', label: 'Guest name' },
];

function getChipForEventRegistrationStatus(status) {
  switch (status) {
    case EVENT_REGISTRATION_STATUS.OPEN:
      return <Chip
        label={status}
        color="success"
      />
    case EVENT_REGISTRATION_STATUS.SCHEDULED:
      return <Chip
        label={status}
        color="default"
      />
    case EVENT_REGISTRATION_STATUS.CLOSE:
      return <Chip
        label={status}
        color="error"
      />
    default:
      return
  }
}

export default function Events() {

  const store = useContext(EventsStoreContext)

  const [columnVisibility, setColumnVisibility] = useState(store.columnVisibility)
  useEffect(() => {
    store.columnVisibility = columnVisibility
  }, [columnVisibility, store])

  const [columnFilters, setColumnFilters] = useState(store.columnFilters)
  useEffect(() => {
    store.columnFilters = columnFilters
  }, [columnFilters, store])

  const [globalFilter, setGlobalFilter] = useTableState(store, 'globalFilter', undefined)
  const [searchField, setSearchField] = useTableState(store, 'searchField', SEARCH_FIELD_OPTIONS[0].value)

  const [pagination, setPagination] = useState(store.pagination)
  useEffect(() => {
    store.pagination = pagination
  }, [pagination, store])

  const currentData = useMemo(() => new Date(), [])
  const navigate = useNavigate()
  const tableContainerRef = useRef(null)

  // Use store for state preservation
  const [open, setOpen] = useState(store.open)
  useEffect(() => {
    store.open = open
  }, [open, store])

  const [isLoading, setIsloading] = useState(store.isLoading)
  useEffect(() => {
    store.isLoading = isLoading
  }, [isLoading, store])

  const [isRefetching, setIsRefetching] = useState(store.isRefetching)
  useEffect(() => {
    store.isRefetching = isRefetching
  }, [isRefetching, store])

  const [allTableData, setAllTableData] = useState(store.allTableData)
  useEffect(() => {
    store.allTableData = allTableData
  }, [allTableData, store])

  const [tableData, setTableData] = useState(store.tableData)
  useEffect(() => {
    store.tableData = tableData
  }, [tableData, store])

  const [allEventCategories, setAllEventCategories] = useState(store.allEventCategories)
  useEffect(() => {
    store.allEventCategories = allEventCategories
  }, [allEventCategories, store])

  const [selectedChip, setSelectedChip] = useState(store.selectedChip)
  useEffect(() => {
    store.selectedChip = selectedChip
  }, [selectedChip, store])

  useEffect(() => {
    setSelectedChip(0)
  }, [setSelectedChip])

  const [columnPinning, setColumnPinning] = useState(store.columnPinning)
  useEffect(() => {
    store.columnPinning = columnPinning
  }, [columnPinning, store])

  useEffect(() => {
    switch (selectedChip) {
      case 0:
        const upcoming = allTableData.filter((item) =>
          item.startDate.getTime() >= currentData.getTime()
        )
        setTableData(
          upcoming.sort(
            (a, b) => a.startDate.getTime() - b.startDate.getTime()
          )
        )
        break
      case 1:
        const past = allTableData.filter((item) =>
          item.startDate.getTime() <= currentData.getTime()
        )
        setTableData(past)
        break
      default:
        setTableData(allTableData)
        break
    }
  }, [selectedChip, allTableData, currentData])

  const upcomingCount = useMemo(
    () => allTableData.filter((item) => item.startDate.getTime() >= currentData.getTime()).length,
    [allTableData, currentData]
  )
  const pastCount = useMemo(
    () => allTableData.filter((item) => item.startDate.getTime() <= currentData.getTime()).length,
    [allTableData, currentData]
  )

  const fetchData = async () => {
    await getSpeakers()
    const events = await getEvents()
    const categories = await getEventCategories()
    setAllEventCategories(categories.map((category) => category.name))
    setIsloading(false)
    setIsRefetching(false)
    setAllTableData(events)
  }

  useEffect(() => {
    // Restore state from store if data exists
    if (store.allTableData.length > 0 && !store.needsRefresh) {
      setAllTableData(store.allTableData)
      setTableData(store.tableData)
      setAllEventCategories(store.allEventCategories)
      setIsloading(false)
      setIsRefetching(false)
    } else {
      // Fetch data if not loaded or refresh is needed
      if (store.needsRefresh) {
        store.needsRefresh = false
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore scroll position after table is rendered and data is loaded
  useRestoreScrollPosition(tableContainerRef, store, tableData, isLoading)

  const getToggleHandler = useMemo(() => createTogglePinHandler(setColumnPinning), [setColumnPinning])
  const columns = useMemo(() => [
    {
      accessorKey: 'title',
      header: 'Event',
      Cell: ({ renderedCellValue, row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img
            alt="avatar"
            height={42}
            width={64}
            src={row.original.image}
            loading="lazy"
          />
          <span>{renderedCellValue}</span>
        </Box>
      )
    },
    {
      accessorFn: (originalRow) => new Date(originalRow.startDate),
      accessorKey: 'startDate',
      header: 'Event Date',
      filterVariant: 'date-range',
      enableGlobalFilter: false,
      Cell: ({ cell }) => dayjs(cell.getValue()).format('DD MMM YYYY'),
    },
    {
      accessorKey: 'venue',
      header: 'Venue',
      enableGlobalFilter: false,
    },
    {
      accessorFn: (originalRow) => originalRow.speakers,
      id: 'speakers',
      header: 'Speakers',
      enableGlobalFilter: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          {cell.getValue().map((speaker) => (
            <Chip
              key={speaker.objectId}
              avatar={<Avatar src={speaker.avatar} />}
              label={speaker.firstName + " " + speaker.lastName}
            />
          ))}
        </Box>
      )
    },
    {
      accessorFn: (originalRow) => originalRow.guests ?? [],
      id: 'guests',
      header: 'Guests',
      enableGlobalFilter: false,
      enableColumnFilter: false,
      Cell: ({ cell }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          {cell.getValue().map((guest) => (
            <Chip
              key={guest.objectId}
              avatar={<Avatar src={guest.avatar} />}
              label={`${guest.firstName || ''} ${guest.lastName || ''}`.trim() || '—'}
            />
          ))}
        </Box>
      )
    },
    {
      accessorKey: 'eventCategories',
      header: 'Event Categories',
      enableGlobalFilter: false,
      filterVariant: 'multi-select',
      filterSelectOptions: allEventCategories,
      Cell: ({ cell }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          {cell.getValue().map((category) => (
            <Chip key={category} label={category} />
          ))}
        </Box>
      )
    },
    {
      accessorKey: 'registrationStatus',
      header: 'Registration Status',
      enableGlobalFilter: false,
      filterVariant: 'select',
      filterSelectOptions: [EVENT_REGISTRATION_STATUS.OPEN, EVENT_REGISTRATION_STATUS.SCHEDULED, EVENT_REGISTRATION_STATUS.CLOSE],
      Cell: ({ renderedCellValue, row }) => (
        getChipForEventRegistrationStatus(renderedCellValue)
      )
    },
    {
      accessorFn: (originalRow) => originalRow.members.length + "/" + originalRow.capacity,
      id: 'membersRegistered',
      header: 'Members Registered',
      enableGlobalFilter: false,
      enableColumnFilter: false
    },
    {
      accessorKey: 'hiddenState',
      header: 'Visibility',
      enableGlobalFilter: false,
      filterVariant: 'select',
      filterSelectOptions: VISIBLE_STATE_CASES.map((value) => value.label),
      Cell: ({ cell }) => (
        <Chip label={cell.getValue()} />
      )
    }
  ], [allEventCategories])

  const columnsWithPin = useMemo(() => {
    return addPinControlsToColumns(columns, columnPinning, getToggleHandler)
  }, [columns, columnPinning, getToggleHandler])

  const getRowSearchValue = useMemo(
    () => (row, field) => {
      if (field === 'speaker') {
        const speakers = Array.isArray(row.speakers) ? row.speakers : []
        return speakers.length > 0
          ? speakers
            .map((s) => `${s.firstName || ''} ${s.lastName || ''}`.trim())
            .filter(Boolean)
            .join(' ')
          : ''
      }
      if (field === 'guest') {
        const guests = Array.isArray(row.guests) ? row.guests : []
        return guests.length > 0
          ? guests
            .map((g) => `${g.firstName || ''} ${g.lastName || ''}`.trim())
            .filter(Boolean)
            .join(' ')
          : ''
      }
      return row[field]
    },
    []
  )
  const data = useFilteredBySearch(tableData, globalFilter, searchField, getRowSearchValue)

  const table = useMaterialReactTable({
    columns: columnsWithPin,
    data,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enableColumnPinning: true,
    state: {
      isLoading: isLoading,
      showProgressBars: isRefetching,
      columnFilters: columnFilters,
      columnVisibility: columnVisibility,
      columnPinning: columnPinning,
      globalFilter: globalFilter,
      showGlobalFilter: false,
      pagination: pagination
    },
    icons: {
      ViewColumnIcon: ColumnsIcon,
    },
    muiFilterDatePickerProps: {
      format: 'DD/MM/YYYY',
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        // Save scroll position before navigation
        if (tableContainerRef.current) {
          store.scrollTop = tableContainerRef.current.scrollTop
        }
        navigate(`/event/${row.original.objectId}`)
      },
      sx: {
        cursor: 'pointer'
      }
    }),
    renderTopToolbar: ({ table }) => (
      <TableToolbarWithSearch
        table={table}
        chips={[
          {
            key: 'upcoming',
            label: 'Upcoming ' + upcomingCount,
            active: selectedChip === 0,
            onClick: () => setSelectedChip(selectedChip === 0 ? undefined : 0),
          },
          {
            key: 'past',
            label: 'Past ' + pastCount,
            active: selectedChip === 1,
            onClick: () => setSelectedChip(selectedChip === 1 ? undefined : 1),
          },
        ]}
        exportFileName="events"
        searchFieldOptions={SEARCH_FIELD_OPTIONS}
        searchField={searchField}
        onSearchFieldChange={setSearchField}
        globalFilter={globalFilter}
        onGlobalFilterChange={setGlobalFilter}
        selectMinWidth={100}
        action={
          <Button
            variant="contained"
            size="large"
            onClick={() => setOpen(true)}
          >
            <Add />
            Add Event
          </Button>
        }
      />
    ),
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid",
        color: "grey.300"
      }
    },
    muiTableContainerProps: {
      ref: tableContainerRef,
      sx: {
        maxHeight: 'calc(100vh - 300px)',
        overflow: 'auto'
      },
      onScroll: (e) => {
        // Save scroll position while scrolling
        if (e.target instanceof HTMLElement) {
          store.scrollTop = e.target.scrollTop
        }
      }
    }
  })

  return (
    <>
      <Box
        paddingTop={2}
        paddingBottom={1}
        display="flex"
        justifyContent="space-between"
      >
        <Typography
          variant="h4"
        >
          Events
        </Typography>
      </Box>

      <MaterialReactTable
        table={table}
      />

      <AddModalView
        isOpen={open}
        setIsOpen={setOpen}
        title={"Add Event"}
        view={
          <AddEvent publish={() => {
            setOpen(false)
            setIsRefetching(true)
            fetchData()
          }} />}
      />
    </>
  )
}
